import { z } from 'zod';
import { apiPost, apiSend } from '@/lib/api';

/**
 * ⚠️ ŚCIEŻKI BEZ `/api` — ten prefiks dokłada `apiSend` (`API_BASE`). Podanie go tutaj daje
 * `/api/api/islands/…` i czyste 404, którego nie widać ani w typach, ani w testach.
 */

/**
 * TRYB WYSPY I JEJ BAZA MASTER — dwie decyzje wdrożeniowe, obie tylko dla konta SDI.
 *
 * ⚠️ DO 2026-08-24 NIE BYŁO CZYM ZMIENIĆ BAZY. `masterServer`/`masterDatabase` ustawiała wyłącznie
 * pierwsza rejestracja agenta, a alert prosił „potwierdź albo popraw" — o czynność, której portal
 * nie miał. Zgłoszone z testówki: do maszyny dograno bazę `pesa`, przepięto na nią SupplyPointa,
 * a w portalu nie było jak za tym pójść.
 */

export const WynikTrybuSchema = z.object({
  id: z.string(),
  code: z.string(),
  tryb: z.enum(['ZWYKLY', 'SMART_SYNC']),
  zmieniono: z.boolean(),
});

export const WynikZmianyBazySchema = z.object({
  id: z.string(),
  code: z.string(),
  masterServer: z.string().nullable(),
  masterDatabase: z.string(),
  wyczyszczono: z.boolean(),
  /** Ile wierszy zniknęło z której tabeli — jedyny ślad tej operacji poza audytem. */
  usunieteWiersze: z.array(z.object({ tabela: z.string(), usunieto: z.number() })),
  /** Integracja HR+ wyłączona razem z czyszczeniem (13.10.2026). `optional` — starsza instancja tego nie oddaje. */
  hrPlusWylaczona: z.boolean().optional(),
});

export type WynikZmianyBazy = z.infer<typeof WynikZmianyBazySchema>;

export async function ustawTrybWyspy(id: string, tryb: 'ZWYKLY' | 'SMART_SYNC') {
  return apiSend('PUT', `/islands/${id}/tryb`, { tryb }, WynikTrybuSchema);
}

/**
 * WYCZYSZCZENIE DANYCH WYSPY BEZ ZMIANY BAZY — „wipe", po którym agent zaciąga wszystko od nowa.
 *
 * ⚠️ ZGŁOSZONE (01.09.2026): „czy możesz tutaj po prostu dodać przycisk czyszczenia danych bez
 * wpisywania wszystkiego od nowa? taki wipe, żeby od nowa zaciągnął agent dane". Czyszczenie było
 * dostępne WYŁĄCZNIE jako pole przy zmianie bazy — czyli wymagało przepisania nazwy bazy, serwera
 * i kodu wyspy, żeby udawać zmianę czegoś, co się nie zmienia.
 *
 * ⚠️ `zmianWKolejce` TO LICZBA OPERACJI ANULOWANYCH TYM WIPEM (od 02.09.2026), a nie tych, które
 * zostają. Wcześniej kolejka przechodziła wipe nietknięta i po wyczyszczeniu wyspy wisiały w niej
 * operacje zbudowane z read-modelu, którego już nie ma. Stara nota niżej opisuje TAMTĄ decyzję —
 * zostaje jako ślad, dlaczego brzmienie pola się zmieniło.
 *
 * ⚠️ (nieaktualne) `zmianWKolejce` NIE JEST OSTRZEŻENIEM O BLOKADZIE. Zmiany w kolejce wykonają się normalnie
 * (adresują wiersze kluczem SPS, nie naszymi identyfikatorami) — ekran mówi o nich, bo pojadą do
 * bazy, której read-model dopiero się odbudowuje.
 */
/**
 * PRZEWINIĘCIE KURSORA WYDAŃ RODZEŃSTWA DO WYBRANEJ CHWILI.
 *
 * ⚠️ ZGŁOSZONE (01.09.2026, IKEA): „przycisk, który zaciągnie od jakiegoś momentu transaction loga
 * ze wszystkich maszyn i wyrówna mastera" — bo włączenie rozsyłania ustawia kursory na bieżący
 * szczyt, więc pobrania z czasu instalacji nie zjeżdżają w dół nigdy.
 */
export const WynikPrzewinieciaSchema = z.object({
  wydanOd: z.string().nullable(),
  doRozeslania: z.number(),
  wezly: z.array(
    z.object({
      nodeId: z.string(),
      kursorPrzed: z.string().nullable(),
      kursorPo: z.string(),
      doRozeslania: z.number(),
      /**
       * SKĄD SIĘ BIERZE `doRozeslania` — po jednej pozycji na automat źródłowy.
       *
       * ⚠️ ZAMÓWIONE 02.09.2026: „kliknięcie na ilość wydań, żeby pokazało skąd ta liczba się
       * wzięła". Suma tych pozycji JEST tą liczbą (serwer tak ją liczy), więc da się je dodać
       * i wyjść na to samo.
       *
       * ⚠️ `optional`, bo instancja starsza od tej zmiany pola nie odda — ekran pokazuje wtedy
       * samą liczbę, bez rozwijania.
       */
      zrodla: z
        .array(z.object({ vmid: z.number(), nazwa: z.string(), wydan: z.number() }))
        .optional(),
    }),
  ),
  pominietych: z.number(),
  rozsylanieWlaczone: z.boolean(),
});

export async function przewinWydania(id: string, od: string) {
  return apiPost(`/islands/${id}/wydania-rodzenstwa/przewin`, { od }, WynikPrzewinieciaSchema);
}

/**
 * WYNIK PORZUCENIA ZALEGŁOŚCI — ile wierszy NIE pojedzie do baz maszyn.
 *
 * ⚠️⚠️ LICZBA JEST TU NAJWAŻNIEJSZĄ CZĘŚCIĄ ODPOWIEDZI, nie potwierdzeniem. To decyzja
 * o tym, że kawał historii zostanie tylko u siebie — więc ekran musi powiedzieć, jak duży.
 */
export const WynikPorzuceniaSchema = z.object({
  wezly: z.array(z.object({ nodeId: z.string(), porzuconych: z.number(), kursorPo: z.string() })),
  porzuconychRazem: z.number(),
});

/**
 * ZDJĘCIE GRANICY CZASU WYDAŃ RODZEŃSTWA.
 *
 * ⚠️⚠️ GRANICĘ USTAWIA PRZEWINIĘCIE „od daty" i NIC jej potem nie zdejmuje, a ona tnie KAŻDĄ
 * paczkę kanału — także bieżącą. Zgłoszone z IKEA Babimost (14.09.2026): kursory nie drgnęły
 * przez trzy doby, węzły pytały co cykl, błędu nie było.
 */
export function zdejmijGraniceWydan(id: string) {
  return apiPost(
    `/islands/${id}/wydania-rodzenstwa/bez-granicy`,
    {},
    z.object({ zdjeto: z.boolean(), bylo: z.string().nullable() }),
  );
}

export function porzucZaleglosc(id: string) {
  return apiPost(`/islands/${id}/wydania-rodzenstwa/porzuc-zaleglosc`, {}, WynikPorzuceniaSchema);
}

export const WynikCzyszczeniaSchema = z.object({
  id: z.string(),
  code: z.string(),
  usunieteWiersze: z.array(z.object({ tabela: z.string(), usunieto: z.number() })),
  wierszy: z.number(),
  zmianWKolejce: z.number(),
  hrPlusWylaczona: z.boolean().optional(),
});

/**
 * `wylaczHrPlus` — decyzja o integracji HR+ przy czyszczeniu (13.10.2026). Serwer ODMAWIA wipe'u
 * wyspy z działającym HR+, gdy jej nie ma, więc ekran wysyła ją zawsze, gdy wyspa ma HR+.
 */
export async function wyczyscDaneWyspy(id: string, opcje: { wylaczHrPlus?: boolean } = {}) {
  return apiPost(`/islands/${id}/wyczysc-dane`, opcje, WynikCzyszczeniaSchema);
}

export async function zmienBazeWyspy(
  id: string,
  dane: { server: string | null; database: string; potwierdzenie: string; wyczyscDane: boolean; wylaczHrPlus?: boolean },
) {
  return apiSend('PUT', `/islands/${id}/baza`, dane, WynikZmianyBazySchema);
}

/**
 * Wynik przełączenia rozsyłania wydań rodzeństwa.
 *
 * ⚠️ `kursorOd` NIE JEST OZDOBĄ: to numer, od którego maszyny zaczną dostawać cudze wiersze.
 * Ekran pokazuje go człowiekowi, bo to jedyna informacja o tym, że historia ZOSTAJE po stronie
 * scalania, a nie leci kanałem.
 */
export const WynikRozsylaniaSchema = z.object({
  wlaczone: z.boolean(),
  kursorOd: z.string().nullable(),
});

export async function ustawRozsylanieWydan(id: string, wlaczone: boolean) {
  return apiSend('PUT', `/islands/${id}/wydania-rodzenstwa`, { wlaczone }, WynikRozsylaniaSchema);
}

/**
 * WSKAZANIE WĘZŁA NA MASTERA WYSPY — gdy master powstał POZA naszym zadaniem odtworzenia.
 *
 * ⚠️ ISTNIEJE, BO INACZEJ WYSPA STOI. `masterAgentId` ustawiało wyłącznie odtworzenie kopii
 * zlecone z portalu, a config-down w Smart Syncu bez tego wskazania WSTRZYMUJE wszystkie
 * operacje dla maszyn. Zmierzone na wdrożeniu PESA (28.08.2026): platforma odtworzyła bazę
 * mastera po swojej stronie, więc wskazania nie było skąd wziąć.
 */
export const WynikWskazaniaSchema = z.object({
  wyspa: z.string(),
  nodeId: z.string(),
  /** `false` = ten węzeł był już masterem; ktoś kliknął dwa razy albo zrobił to wcześniej. */
  zmieniono: z.boolean(),
});

/**
 * POTWIERDZENIE BAZY MASTER, KTORA WYSPA JUZ MA — bez zmiany nazwy.
 *
 * ⚠️ ISTNIEJE, BO ALERT `MASTER_WYSPY_DO_ZATWIERDZENIA` mowil „Potwierdz albo popraw", a POTWIERDZIC
 * nie bylo czym: `masterConfirmed` ustawiala wylacznie zmiana bazy, czyli przepisanie tej samej
 * nazwy plus kodu wyspy. Zgloszone z instancji w trybie zwyklym (28.08.2026).
 */
export async function potwierdzBazeWyspy(id: string) {
  return apiSend(
    'PUT',
    `/islands/${id}/baza-potwierdz`,
    undefined,
    z.object({ potwierdzona: z.boolean(), baza: z.string() }),
  );
}

export async function wskazMasteraWyspy(id: string, agentId: string) {
  return apiSend('PUT', `/islands/${id}/master-wezel`, { agentId }, WynikWskazaniaSchema);
}
